import React, { useState } from "react";
import { Card, DatePicker, Button, Row, Col, message, Table } from "antd";
import dayjs from "dayjs";
import axios from "axios";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  ChartDataLabels
);

const { RangePicker } = DatePicker;

const UserActivitySummary = () => {
  // Set default dates: start and end of current month
  const defaultDates = [dayjs().startOf("month"), dayjs().endOf("month")];
  const [dates, setDates] = useState(defaultDates);
  const [loading, setLoading] = useState(false);
  const [activityData, setActivityData] = useState([]);
  const [prevActivityData, setPrevActivityData] = useState([]);
  const [userActivityList, setUserActivityList] = useState([]);

  // Helper to format date as dd-MM-yyyy
  const formatDate = (date) => dayjs(date).format("DD-MM-YYYY");

  // Helper to get previous period range
  const getPrevRange = (from, to) => {
    const diff = to.diff(from, "day") + 1;
    const prevTo = from.subtract(1, "day");
    const prevFrom = prevTo.subtract(diff - 1, "day");
    return [prevFrom, prevTo];
  };

  const handleLoad = async () => {
    if (!dates || dates.length !== 2) return;
    setLoading(true);
    setActivityData([]);
    setPrevActivityData([]);
    setUserActivityList([]);
    try {
      // Current period
      const from = formatDate(dates[0]);
      const to = formatDate(dates[1]);
      const tokenString = localStorage.getItem("token");
      const response = await axios.get(
        `${global.config.APIURL.BaseURL}api/User/GetActiveUsersbyEmployee?From=${from}&To=${to}`,
        {
          headers: {
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      setActivityData(response.data || []);

      // Previous period
      const [prevFromDayjs, prevToDayjs] = getPrevRange(dates[0], dates[1]);
      const prevFrom = formatDate(prevFromDayjs);
      const prevTo = formatDate(prevToDayjs);
      const prevResponse = await axios.get(
        `${global.config.APIURL.BaseURL}api/User/GetActiveUsersbyEmployee?From=${prevFrom}&To=${prevTo}`,
        {
          headers: {
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      setPrevActivityData(prevResponse.data || []);

      // User activity list for current period
      const userListResponse = await axios.get(
        `${global.config.APIURL.BaseURL}api/User/GetActiveUsersListbyEmployeeandPeriod?From=${from}&To=${to}`,
        {
          headers: {
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      setUserActivityList(userListResponse.data || []);
    } catch (error) {
      message.error("Failed to load activity summary.");
      setActivityData([]);
      setPrevActivityData([]);
      setUserActivityList([]);
    } finally {
      setLoading(false);
    }
  };

  // Prepare data for chart (sort by date ascending)
  const sortedActivityData = [...activityData].sort(
    (a, b) => dayjs(a.Date).valueOf() - dayjs(b.Date).valueOf()
  );
  const sortedPrevActivityData = [...prevActivityData].sort(
    (a, b) => dayjs(a.Date).valueOf() - dayjs(b.Date).valueOf()
  );

  // For each current period date, get the previous period value by offset
  const prevDataForCurrent = sortedActivityData.map((item, idx) => {
    const offset = dayjs(item.Date).diff(dayjs(sortedActivityData[0]), "day");
    const prevDate = dayjs(sortedPrevActivityData[0]).add(offset, "day");
    const prevItem = sortedPrevActivityData.find(
      (p) =>
        dayjs(p.Date).format("DD-MM-YYYY") === prevDate.format("DD-MM-YYYY")
    );
    return prevItem ? prevItem.ActiveUserCount : null;
  });

  const chartData = {
    labels: sortedActivityData.map((item) =>
      dayjs(item.Date).format("DD-MM-YYYY")
    ),
    datasets: [
      {
        label: "Current Period",
        data: sortedActivityData.map((item) => item.ActiveUserCount),
        fill: false,
        borderColor: "#1677ff",
        backgroundColor: "#1677ff",
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: "Previous Period",
        data: prevDataForCurrent,
        fill: false,
        borderColor: "#ff9900",
        backgroundColor: "#ff9900",
        borderDash: [6, 6],
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: true, position: "top" },
      tooltip: { enabled: true },
      datalabels: {
        display: true,
        color: "#222",
        anchor: "end",
        align: "top",
        font: { weight: "bold" },
        formatter: function (value) {
          return value;
        },
      },
    },
    scales: {
      x: { title: { display: true, text: "Date" } },
      y: {
        title: { display: true, text: "Active User Count" },
        beginAtZero: true,
      },
    },
  };

  // Table columns for user activity list
  const userTableColumns = [
    {
      title: "Firm Name",
      dataIndex: "FirmName",
      key: "FirmName",
      render: (text) => text || "-",
    },
    {
      title: "Owner Name",
      dataIndex: "OwnerName",
      key: "OwnerName",
    },
    {
      title: "Activity Count",
      dataIndex: "ActivityCount",
      key: "ActivityCount",
      sorter: (a, b) => a.ActivityCount - b.ActivityCount,
      defaultSortOrder: "descend",
    },
  ];

  return (
    <Card
      title="User Activity Summary"
      style={{ maxWidth: 900, margin: "32px auto" }}
    >
      <Row gutter={16} align="middle" style={{ marginBottom: 24 }}>
        <Col flex="none">
          <RangePicker
            value={dates}
            onChange={setDates}
            style={{ width: 240 }}
            allowClear={false}
            format="DD-MM-YYYY"
            inputReadOnly
          />
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={handleLoad}
            loading={loading}
            disabled={!dates || dates.length !== 2}
          >
            Load
          </Button>
        </Col>
      </Row>
      <div style={{ marginBottom: 32 }}>
        <Line data={chartData} options={chartOptions} height={80} />
      </div>
      <Table
        columns={userTableColumns}
        dataSource={userActivityList}
        rowKey={(record, idx) => record.FirmName + record.OwnerName + idx}
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="middle"
        title={() => "User Activity List"}
        style={{ marginTop: 32 }}
      />
    </Card>
  );
};

export default UserActivitySummary;
